import { HistoryMovement } from 'types';

import {
  ConstitutionExcelPreview,
  ConstitutionExcelPreviewItem,
  ConstitutionExcelRow,
  ConstitutionExcelValidationError,
} from '../types/ConstitutionExcel';
import {
  assignConstitutionExcelRanges,
  getRangeMode,
} from './assignConstitutionExcelRanges';
import { HoldingClassForConstitutionExcel } from './initConstitutionNumerations';

type MapConstitutionExcelToPreviewParams = {
  rows: ConstitutionExcelRow[];
  holdingClasses: HoldingClassForConstitutionExcel[];
  errors?: ConstitutionExcelValidationError[];
};

const getRowIndexesWithErrors = (
  errors: ConstitutionExcelValidationError[]
): Set<number> => {
  const rowIndexes = new Set<number>();

  errors.forEach((error) => {
    if (error.rowIndex) {
      rowIndexes.add(error.rowIndex);
    }
  });

  return rowIndexes;
};

const sortPreviewItemsByRange = (
  items: ConstitutionExcelPreviewItem[]
): ConstitutionExcelPreviewItem[] => {
  const hasAssignedRanges = items.some((item) => item.shareFrom > 0);

  if (!hasAssignedRanges) {
    return items;
  }

  return [...items].sort((itemA, itemB) => {
    if (itemA.shareFrom === 0 && itemB.shareFrom !== 0) return 1;
    if (itemB.shareFrom === 0 && itemA.shareFrom !== 0) return -1;
    if (itemA.shareFrom !== itemB.shareFrom) {
      return itemA.shareFrom - itemB.shareFrom;
    }

    const classCompare = itemA.holdingClassName.localeCompare(
      itemB.holdingClassName
    );
    if (classCompare !== 0) return classCompare;

    return itemA.rowIndex - itemB.rowIndex;
  });
};

const countUniqueCifs = (
  items: ConstitutionExcelPreviewItem[],
  predicate: (item: ConstitutionExcelPreviewItem) => boolean
): number => {
  const cifs = new Set<string>();

  items.forEach((item) => {
    if (predicate(item)) {
      cifs.add(item.cif);
    }
  });

  return cifs.size;
};

export const mapConstitutionExcelToPreview = ({
  rows,
  holdingClasses,
  errors = [],
}: MapConstitutionExcelToPreviewParams): ConstitutionExcelPreview => {
  const rowIndexesWithErrors = getRowIndexesWithErrors(errors);

  const { items: assignedItems } = assignConstitutionExcelRanges({
    rows,
    holdingClasses,
    rowIndexesWithErrors,
  });

  const items = sortPreviewItemsByRange(assignedItems);

  const historyMovements: HistoryMovement[] = items
    .filter((item) => item.shareFrom > 0 && item.shareTo >= item.shareFrom)
    .map((item) => ({
      from: item.shareFrom,
      to: item.shareTo,
      holdingClassName: item.holdingClassName,
      partnerNameSeller: item.partnerName,
    }));

  const validItems = items.filter(
    (item) => !rowIndexesWithErrors.has(item.rowIndex)
  );

  return {
    items,
    historyMovements,
    totalShares: validItems.reduce((acc, item) => acc + item.shares, 0),
    totalPartners: countUniqueCifs(items, () => true),
    totalNewPartners: countUniqueCifs(items, (item) => !item.isExistingPartner),
    totalExistingPartners: countUniqueCifs(
      items,
      (item) => item.isExistingPartner
    ),
    errors,
    rangeMode: getRangeMode(rows),
  };
};
